import { useEffect, useRef, useState, type FormEvent } from "react";

import {
  recoveryActionForBarrier,
  type BreakMinutes,
  type RecoveryAction,
  type RecoveryBarrier,
  type StudentRecoverySnapshot,
} from "@vsta/contracts";

import type { FocusBlockConnectivity } from "./focus-block.js";

export type RecoveryUiCommand =
  | { intent: "start-break"; plannedMinutes: BreakMinutes }
  | { intent: "end-break"; breakId: string }
  | { intent: "im-stuck" }
  | { intent: "return-to-step"; transitionId: string }
  | {
      intent: "record-recovery";
      transitionId: string;
      barrier: RecoveryBarrier;
      action: RecoveryAction;
    }
  | { intent: "park-thought"; text: string };

type Props = {
  connectivity: FocusBlockConnectivity;
  monotonicNow?: () => number;
  onCommand: (command: RecoveryUiCommand) => Promise<void>;
  repaintIntervalMs?: number;
  snapshot: StudentRecoverySnapshot;
};

const barrierLabels: Record<RecoveryBarrier, string> = {
  "unclear-directions": "Unclear directions",
  "step-too-large": "Step feels too large",
  "technical-issue": "Technical issue",
  distraction: "Distraction",
  "fatigue-unwell": "Tired or not feeling well",
  "waiting-for-help": "Waiting for help",
  other: "Something else",
};

const actionLabels: Record<RecoveryAction, string> = {
  "reread-directions": "Reread the directions and name the first action.",
  "work-one-small-part": "Work on one smaller part of this Step.",
  "retry-resource": "Retry the Resource without changing the Active Step.",
  "restart-for-five": "Restart with one five-minute Focus Block.",
  "take-five-minute-break": "Take one five-minute Break, then return here.",
  "request-help": "Request help and keep this Step available.",
  "continue-active-step": "Return to the Active Step with one next action.",
};

function formatDuration(durationMs: number) {
  const seconds = Math.max(0, Math.floor(durationMs / 1_000));
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

function connectivityCopy(connectivity: FocusBlockConnectivity) {
  if (connectivity === "offline") {
    return "Offline · saved information remains visible; new changes cannot be confirmed.";
  }
  if (connectivity === "reconnecting") {
    return "Reconnecting · wait for the current Break and transition state.";
  }
  return "Connected · Break and transition records are synchronized.";
}

export function BreakRecoveryPanel({
  connectivity,
  monotonicNow = () => performance.now(),
  onCommand,
  repaintIntervalMs = 1_000,
  snapshot,
}: Props) {
  const anchorKey = `${snapshot.revision}:${snapshot.observedAt}`;
  const anchor = useRef<{ key: string; monotonicTime: number } | null>(null);
  if (anchor.current === null || anchor.current.key !== anchorKey) {
    anchor.current = { key: anchorKey, monotonicTime: monotonicNow() };
  }
  const [, repaint] = useState(0);
  const [pending, setPending] = useState<RecoveryUiCommand["intent"] | null>(
    null,
  );
  const [announcement, setAnnouncement] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [barrier, setBarrier] = useState<RecoveryBarrier>("unclear-directions");
  const [parkingText, setParkingText] = useState("");
  const recoveryHeading = useRef<HTMLHeadingElement>(null);
  const previousBreak = useRef(snapshot.break);

  const hasRunningClock =
    snapshot.break?.state === "running" ||
    snapshot.transition?.state === "running";
  useEffect(() => {
    if (!hasRunningClock) return;
    const timer = window.setInterval(
      () => repaint((value) => value + 1),
      repaintIntervalMs,
    );
    return () => window.clearInterval(timer);
  }, [hasRunningClock, repaintIntervalMs]);

  const projectedMs = Math.max(
    0,
    monotonicNow() - anchor.current.monotonicTime,
  );
  const breakDurationMs =
    (snapshot.break?.breakDurationMs ?? 0) +
    (snapshot.break?.state === "running" ? projectedMs : 0);
  const transitionDurationMs =
    (snapshot.transition?.transitionDurationMs ?? 0) +
    (snapshot.transition?.state === "running" ? projectedMs : 0);
  const recoveryDue =
    snapshot.transition?.state === "running" &&
    (snapshot.transition.recoveryDue ||
      Date.parse(snapshot.observedAt) + projectedMs >=
        Date.parse(snapshot.transition.recoveryDueAt));

  useEffect(() => {
    if (recoveryDue) recoveryHeading.current?.focus();
  }, [recoveryDue]);

  useEffect(() => {
    const prior = previousBreak.current;
    const current = snapshot.break;
    if (prior?.state !== "running" && current?.state === "running") {
      setAnnouncement(`${current.plannedMinutes}-minute Break started.`);
    } else if (prior?.state === "running" && current?.state !== "running") {
      setAnnouncement(
        snapshot.transition?.state === "running"
          ? "Break ended. Transition started."
          : "Break ended.",
      );
    }
    previousBreak.current = current;
  }, [snapshot.break, snapshot.transition?.state]);

  const controlsDisabled = pending !== null || connectivity !== "connected";

  async function execute(command: RecoveryUiCommand, success: string) {
    setPending(command.intent);
    setError(null);
    setAnnouncement("");
    try {
      await onCommand(command);
      setAnnouncement(success);
      if (command.intent === "park-thought") setParkingText("");
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "That change was not confirmed. Try again after synchronization returns.",
      );
    } finally {
      setPending(null);
    }
  }

  function saveParkingLot(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const text = parkingText.trim();
    if (text.length === 0) {
      setError("Enter a thought or later action before saving it.");
      return;
    }
    void execute(
      { intent: "park-thought", text },
      "Saved in the Parking Lot. The Active Step did not change.",
    );
  }

  const selectedAction = recoveryActionForBarrier[barrier];
  const transition = snapshot.transition;

  return (
    <section className="break-recovery-panel" aria-labelledby="support-title">
      <div className="break-recovery-heading">
        <div>
          <p className="status-label">Break and recovery</p>
          <h3 id="support-title">Keep the next move clear</h3>
        </div>
        <span className={`timer-connectivity connectivity-${connectivity}`}>
          {connectivityCopy(connectivity)}
        </span>
      </div>

      {announcement === "" ? null : (
        <p role="status" aria-live="polite" className="recovery-command-status">
          {announcement}
        </p>
      )}
      {error === null ? null : (
        <p role="alert" className="focus-command-error">
          {error}
        </p>
      )}

      {snapshot.break?.state === "running" ? (
        <section
          className="current-break"
          aria-labelledby="current-break-title"
        >
          <p className="status-label">Break in progress</p>
          <h4 id="current-break-title">
            {snapshot.break.plannedMinutes}-minute Break
          </h4>
          <p className="support-timer" aria-label="Break time">
            {formatDuration(breakDurationMs)}
          </p>
          <p>
            Break time is recorded separately from Focus Block timer-running
            time.
          </p>
          <button
            type="button"
            disabled={controlsDisabled}
            onClick={() =>
              void execute(
                { intent: "end-break", breakId: snapshot.break!.id },
                "Break ended. Transition time started.",
              )
            }
          >
            {pending === "end-break" ? "Ending Break…" : "End Break"}
          </button>
        </section>
      ) : null}

      {transition?.state === "running" ? (
        <section
          className="current-transition"
          aria-labelledby="transition-title"
        >
          <p className="status-label">Transition</p>
          <h4 id="transition-title">
            Choose the next action when you’re ready
          </h4>
          <p className="support-timer" aria-label="Transition time">
            {formatDuration(transitionDurationMs)}
          </p>
          <div className="recovery-actions">
            <button
              type="button"
              disabled={controlsDisabled}
              onClick={() =>
                void execute(
                  { intent: "return-to-step", transitionId: transition.id },
                  "Returned to the Active Step.",
                )
              }
            >
              Return to Step
            </button>
            <button
              className="secondary-button"
              type="button"
              disabled={controlsDisabled || recoveryDue}
              onClick={() =>
                void execute(
                  { intent: "im-stuck" },
                  "Recovery Check-in is ready.",
                )
              }
            >
              I'm stuck
            </button>
          </div>
          {!recoveryDue ? (
            <p>
              A Recovery Check-in becomes available after five transition
              minutes, or choose I’m stuck now.
            </p>
          ) : (
            <form
              className="recovery-check-in"
              onSubmit={(event) => {
                event.preventDefault();
                void execute(
                  {
                    intent: "record-recovery",
                    transitionId: transition.id,
                    barrier,
                    action: selectedAction,
                  },
                  `Next action saved: ${actionLabels[selectedAction]}`,
                );
              }}
            >
              <h4 ref={recoveryHeading} tabIndex={-1}>
                What got in the way?
              </h4>
              <p>Choose the closest answer. This helps select one next move.</p>
              <fieldset>
                <legend>Barrier</legend>
                {(Object.keys(barrierLabels) as RecoveryBarrier[]).map(
                  (value) => (
                    <label key={value} className="barrier-option">
                      <input
                        type="radio"
                        name="barrier"
                        value={value}
                        checked={barrier === value}
                        onChange={() => setBarrier(value)}
                      />
                      <span>{barrierLabels[value]}</span>
                    </label>
                  ),
                )}
              </fieldset>
              <div className="next-action-preview">
                <strong>Next action</strong>
                <p>{actionLabels[selectedAction]}</p>
              </div>
              <button type="submit" disabled={controlsDisabled}>
                {pending === "record-recovery"
                  ? "Saving next action…"
                  : "Use this next action"}
              </button>
            </form>
          )}
        </section>
      ) : null}

      {snapshot.break?.state !== "running" &&
      transition?.state !== "running" ? (
        <div className="break-actions" aria-label="Break and recovery actions">
          {([5, 10] as const).map((plannedMinutes) => (
            <button
              key={plannedMinutes}
              className="secondary-button"
              type="button"
              disabled={controlsDisabled}
              onClick={() =>
                void execute(
                  { intent: "start-break", plannedMinutes },
                  `${plannedMinutes}-minute Break started.`,
                )
              }
            >
              Take a {plannedMinutes}-minute Break
            </button>
          ))}
          <button
            type="button"
            disabled={controlsDisabled}
            onClick={() =>
              void execute(
                { intent: "im-stuck" },
                "Recovery Check-in is ready.",
              )
            }
          >
            I'm stuck
          </button>
        </div>
      ) : null}

      <section className="parking-lot" aria-labelledby="parking-title">
        <h4 id="parking-title">Parking Lot</h4>
        <p>Save something for later without leaving the Active Step.</p>
        <form onSubmit={saveParkingLot}>
          <label htmlFor="parking-lot-text">Thought or later action</label>
          <textarea
            id="parking-lot-text"
            value={parkingText}
            maxLength={500}
            rows={2}
            onChange={(event) => setParkingText(event.currentTarget.value)}
          />
          <button type="submit" disabled={controlsDisabled}>
            {pending === "park-thought" ? "Saving…" : "Save for later"}
          </button>
        </form>
        {snapshot.parkingLotEntries.length === 0 ? (
          <p className="empty-support-note">Nothing saved for later.</p>
        ) : (
          <ul className="parking-list">
            {snapshot.parkingLotEntries.map((entry) => (
              <li key={entry.id}>{entry.text}</li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}
